<template>
  <div ref="weatherContainer" class="weather-container" :style="{ backgroundColor: background }">
    <div v-if="loading" class="loading">
      <img src="@/assets/img/weather/loader.svg" alt="loading" />
    </div>

    <div v-else-if="error" class="weather-error">Unable to fetch weather data</div>

    <div v-else :style="containerStyle">
      <div
        v-if="!app.weather || app.weather?.type === WEATHER_DISPLAY_TYPE.NOW.value"
        class="weather-simple"
        :class="{ nowNoLocation: isNowNoLocationType }"
      >
        <div class="now-text" :class="{ noLocation: !showLocationText }">
          <div v-if="showLocationText" class="now-city" :style="titleStyle">
            {{ language === WEATHER_LANGUAGES.ENGLISH.value ? 'Now' : 'Nu' }}
          </div>

          <div
            v-show="currentTemp || currentTemp === 0"
            class="now-temperature"
            :style="nowTempStyle"
          >
            {{ currentTemp }}<sup>&deg;</sup>
          </div>

          <div v-if="showLocationText" class="now-city" :style="titleStyle">
            {{ cityName }}
          </div>
        </div>

        <div v-if="isNowNoLocationType" :style="nowSpaceStyle"></div>

        <div class="now-icon" :style="nowIconStyle">
          <WeatherIcons :iconId="weatherData[0]?.WeatherIcon" :color="app.weather?.iconColor" />
        </div>
      </div>

      <div v-else class="weather-multiple" :class="{ noLocation: !showLocationText }">
        <div v-if="showLocationText" class="multiple-title" :style="titleStyle">
          {{ multipleTitle }}
        </div>

        <div class="multiple-items" :style="multipleItemsContainerStyle">
          <div
            class="item-container"
            v-for="(item, index) of weatherItems"
            :key="index"
            :style="itemStyle"
          >
            <div class="multiple-center">
              <div class="multiple-temperature">
                <div class="temperature" :style="multipleNumbersStyle">
                  {{ item.temperature }}
                </div>

                <div class="lower-temperature" :style="lowerTempStyle">
                  {{ item.minTemperature }}
                </div>
              </div>

              <div class="multiple-icon" :style="multipleIconStyle">
                <WeatherIcons
                  :iconId="item.icon"
                  :color="app.weather ? app.weather.iconColor : defaultValues.iconColor"
                />
              </div>

              <div class="multiple-time" :style="multipleNumbersStyle">
                {{ app.weather?.type === WEATHER_DISPLAY_TYPE.HOURS.value ? item.hour : item.day }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="showLocationText" class="now-city" :style="titleStyle">
          {{ cityName }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  import { debounce } from 'lodash';
  import { computed, ref, toRef } from 'vue';
  import { useResizeObserver } from '@vueuse/core';

  import WeatherIcons from '@/components/apps/weather/WeatherIcons.vue';

  import { timezone } from '@/config';
  import { apiWeatherCurrentConditions } from '@/api/weather';

  import {
    WEATHER_APP_SCALES,
    WEATHER_DISPLAY_TYPE,
    WEATHER_HOURS_FORMAT,
    WEATHER_TEMPERATURE,
    weatherDefaultOptions,
  } from '@/constant/weatherApp';
  import { weatherIcons, WEATHER_DAY_FORMAT, WEATHER_LANGUAGES } from '@/constant/weatherApp';

  export default {
    name: 'WeatherView',

    components: {
      WeatherIcons,
    },

    props: {
      app: {
        type: Object,
        default: () => null,
      },
    },

    data() {
      return {
        loading: true,
        error: false,
        appData: null,
        weatherData: null,
        fetcherTimer: null,
        currentLocation: null,
        weatherItems: [],
        WEATHER_DISPLAY_TYPE,
        WEATHER_LANGUAGES,
        defaultValues: weatherDefaultOptions,
      };
    },

    filters: {
      round(num) {
        return parseInt(num).toFixed(0);
      },
    },

    mounted() {
      this.initWeatherView();

      this.fetcherTimer = setInterval(this.fetchData, 10 * 60 * 1000);
    },

    beforeDestroy() {
      clearInterval(this.fetcherTimer);
    },

    watch: {
      location(value) {
        if (!value || value.length === 0) {
          this.currentLocation = null;
          return;
        }

        const [x, y] = this.currentLocation || [];
        const [newX, newY] = value;

        if (!!this.currentLocation && x === newX && y === newY) return;

        this.currentLocation = value;
        this.initWeatherView();
      },

      measurementSystem() {
        if (!this.app.weather || this.app.weather.type === WEATHER_DISPLAY_TYPE.NOW.value) return;

        this.initWeatherView();
      },

      displayType() {
        this.initWeatherView();
      },

      is24Format() {
        if (this.app.weather?.type !== WEATHER_DISPLAY_TYPE.NOW.value) this.setWeatherItems();
      },

      dayFormat() {
        if (this.app.weather?.type === WEATHER_DISPLAY_TYPE.DAYS.value) this.setWeatherItems();
      },

      app() {
        const isNowData = typeof this.weatherData === 'array' && this.weatherData.length <= 1;

        if (this.app.weather && !isNowData && !this.loading) this.setWeatherItems();
      },
    },

    computed: {
      background() {
        return this.app.weather
          ? this.app.weather.backgroundColor
          : this.app.background?.color || weatherDefaultOptions.backgroundColor;
      },

      containerStyle() {
        const { width, height } = this.getContainerSize();

        const paddingBottom = height * this.currentScales.padding.bottom;
        const paddingLeft = width * this.currentScales.padding.left;
        const paddingRight = width * this.currentScales.padding.right;
        const paddingTop = height * this.currentScales.padding.top;

        return {
          color: this.app.weather?.fontColor,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingBottom: `${paddingBottom}px`,
          paddingLeft: `${paddingLeft}px`,
          paddingRight: `${paddingRight}px`,
          paddingTop: `${paddingTop}px`,
          fontFamily: this.app.weather?.textFont || 'Poppins-600',
          height: height ? `${height}px` : '100%',
          width: width ? `${width}px` : '100%',
        };
      },

      multipleTitle() {
        const isHours = this.app.weather?.type === WEATHER_DISPLAY_TYPE.HOURS.value;

        if (this.app.weather.language === WEATHER_LANGUAGES.SWEDISH.value) {
          return isHours ? 'Kommande timmar' : 'Kommande vecka';
        }

        return isHours ? 'The next hours' : 'The next days';
      },

      currentLocationScale() {
        return WEATHER_APP_SCALES[this.showLocationText ? 'location' : 'noLocation'];
      },

      nowTempStyle() {
        const { height } = this.getContainerSize();

        return {
          fontSize: `${height * this.currentLocationScale.now.numbers.fontSize}px`,
          lineHeight: `${height * this.currentLocationScale.now.numbers.lineHeight}px`,
        };
      },

      nowIconStyle() {
        const adjustedIconWidth = this.getNowIconDimensions();

        return {
          width: `${adjustedIconWidth}px`,
        };
      },

      nowSpaceStyle() {
        if (!this.isNowNoLocationType) return {};

        const { width: containerWidth, height: containerHeight } = this.getContainerSize() || 0;

        const iconWidth = this.getNowIconDimensions();
        const textWidth = containerHeight * 1.3 * this.currentLocationScale.now.numbers.fontSize;
        const paddingLeft = containerWidth * this.currentScales.padding.left;
        const paddingRight = containerWidth * this.currentScales.padding.right;

        const spaceTotalWidth =
          containerWidth - (paddingLeft + paddingRight + iconWidth + textWidth);

        return {
          width: `${spaceTotalWidth * (this.app.weather.widthSeparationPercentage / 100 || 1)}px`,
        };
      },

      titleStyle() {
        const { height } = this.getContainerSize();

        return {
          fontSize: `${height * this.currentLocationScale.now.title.fontSize}px`,
          lineHeight: `${height * this.currentLocationScale.now.title.lineHeight}px`,
          color: this.app.weather?.fontColor,
        };
      },

      multipleIconStyle() {
        return {
          width: `${this.getContainerSize()?.width * this.currentLocationScale.multiple.icon}px`,
        };
      },

      itemStyle() {
        return {
          minWidth: `${
            this.getContainerSize()?.width * this.currentLocationScale.multiple.itemWidth
          }px`,
        };
      },

      multipleItemsContainerStyle() {
        const { height } = this.getContainerSize();

        return {
          height: `${height * this.currentLocationScale.multiple.bodyHeight[app.weather?.type]}px`,
        };
      },

      multipleNumbersStyle() {
        const { height } = this.getContainerSize();

        return {
          fontSize: `${height * this.currentLocationScale.multiple.numbers.fontSize}px`,
          lineHeight: `${
            height *
            this.currentLocationScale.multiple.numbers.lineHeight *
            (this.is24Format ? 1 : 0.75)
          }px`,
          color: this.app.weather?.fontColor,
        };
      },

      lowerTempStyle() {
        const { height } = this.getContainerSize();

        return {
          fontSize: `${height * this.currentLocationScale.multiple.lowerTemp.fontSize}px`,
          lineHeight: `${height * this.currentLocationScale.multiple.lowerTemp.lineHeight}px`,
          color: this.app.weather?.fontColor,
        };
      },

      showLocationText() {
        return this.app.weather.showLocationText !== false;
      },

      displayType() {
        return this.app.weather?.type;
      },

      location() {
        return this.app.weather?.location?.coordinates || this.app.location || [];
      },

      cityName() {
        return this.app.weather?.location?.label || this.weatherData?.[0].WeatherText;
      },

      measurementSystem() {
        const temperature = this.app.weather?.temperatureUnit || this.app.temp_unit;

        return !this.app.weather || temperature === WEATHER_TEMPERATURE.CELSIUS.value
          ? 'Metric'
          : 'Imperial';
      },

      is24Format() {
        return parseInt(this.app.weather?.timeFormat || this.app.time_format) === 24;
      },

      currentDate() {
        return moment(this.clockObj).format('MM/DD/YYYY');
      },

      currentTemp() {
        if (this.app.weather?.type && this.app.weather?.type !== WEATHER_DISPLAY_TYPE.NOW.value)
          return '';

        const temperature = this.weatherData[0]?.Temperature[this.measurementSystem].Value;

        if (!temperature && temperature !== 0) return '';

        return Math.floor(temperature);
      },

      language() {
        return this.app.weather?.language || this.app.lang;
      },

      timeFormat() {
        return this.app.weather?.timeFormat || this.app.time_format || 24;
      },

      weatherIcon() {
        if (!this.weatherData[0] && !this.weatherItems[0]) return {};

        const iconCode = this.weatherData[0]?.WeatherIcon || this.weatherItems[0].icon;

        return weatherIcons[iconCode];
      },

      dayFormat() {
        if (this.app.weather?.dayFormat === WEATHER_DAY_FORMAT.SHORT.value) {
          return WEATHER_DAY_FORMAT.SHORT.format;
        } else if (this.app.weather?.dayFormat === WEATHER_DAY_FORMAT.DAY_OF_MONTH.value) {
          return WEATHER_DAY_FORMAT.DAY_OF_MONTH.format;
        }

        return WEATHER_DAY_FORMAT.WORD.format;
      },

      isNowNoLocationType() {
        return this.app.weather?.type === WEATHER_DISPLAY_TYPE.NOW.value && !this.showLocationText;
      },
    },

    methods: {
      async initWeatherView() {
        this.error = false;
        this.loading = true;

        try {
          await this.fetchData();
        } catch (error) {
          console.log('error: ', error);
          this.error = true;
        }

        this.loading = false;
      },

      async fetchData() {
        try {
          this.weatherData = await apiWeatherCurrentConditions(
            this.location.join(','),
            this.language,
            this.app.weather?.type,
            this.measurementSystem === 'Metric',
          );

          this.setWeatherItems();
        } catch (error) {
          console.log('Error Fetching weather data', error);
        }
      },

      setWeatherItems() {
        if (!this.app.weather || this.app.weather.type === WEATHER_DISPLAY_TYPE.NOW.value) {
          this.weatherItems = [];
          return;
        }
        const isDailyForecast = this.app.weather?.type === WEATHER_DISPLAY_TYPE.DAYS.value;

        const items = isDailyForecast
          ? this.weatherData.DailyForecasts
          : this.weatherData.slice(0, 7);

        this.weatherItems = (items || []).map((weather) => {
          const hourFormat = this.is24Format
            ? WEATHER_HOURS_FORMAT.TWENTY_FOUR.format
            : WEATHER_HOURS_FORMAT.TWELVE.format;
          const temperature = isDailyForecast
            ? `${Math.floor(weather.Temperature.Maximum.Value)}º`
            : `${Math.floor(weather.Temperature.Value)}º`;

          return {
            temperature,
            minTemperature: weather.Temperature.Minimum
              ? `${Math.floor(weather.Temperature.Minimum.Value)}º`
              : '',
            icon: isDailyForecast ? weather.Day.Icon : weather.WeatherIcon,
            hour: isDailyForecast ? '' : moment(weather.DateTime).format(hourFormat),
            day: moment(isDailyForecast ? weather.Date : weather.DateTime).format(this.dayFormat),
          };
        });
      },

      updateClock() {
        moment.locale(this.language);
        this.clockObj = moment();
        this.clockObj.tz(timezone);
      },

      clockTick() {
        this.updateClock();
      },

      getNowIconDimensions() {
        const { width: iconWidth = 100, height: iconHeight = 100 } = this.weatherIcon;
        const widthSize = this.getContainerSize()?.width * this.currentLocationScale.now.icon.width;

        return 0.85 * iconWidth > iconHeight ? widthSize : widthSize * 0.85;
      },
    },

    setup(props) {
      const app = toRef(props, 'app');

      function getCurrentScales() {
        const isNowType = app.value.weather?.type === WEATHER_DISPLAY_TYPE.NOW.value;
        const scaleType = isNowType ? 'now' : 'multiple';

        return this.currentLocationScale[scaleType];
      }

      const weatherContainer = ref(null);
      const containerHeight = ref(null);
      const containerWidth = ref(null);

      const currentScales = computed(getCurrentScales);

      function getContainerSize() {
        const maxHeight =
          containerHeight.value / containerWidth.value < currentScales.value.container
            ? containerHeight.value
            : containerWidth.value * currentScales.value.container;
        const maxWidth = maxHeight / currentScales.value.container;

        return {
          height: maxHeight,
          width: maxWidth,
        };
      }

      useResizeObserver(
        weatherContainer,
        debounce((entries) => {
          const container = entries[0];
          containerHeight.value = container.contentRect.height;
          containerWidth.value = container.contentRect.width;
        }, 500),
      );

      return {
        containerHeight,
        containerWidth,
        currentScales,

        // methods
        getContainerSize,
        weatherContainer,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .loading,
  .weather-error {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;

    img {
      width: 40px;
      height: 40px;
    }
  }

  .weather-container {
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
    align-items: center;
    font-family: 'Poppins';
    color: $primaryText;

    .now-city {
      color: $secondaryText;
      text-align: left;
      white-space: normal;
    }

    .multiple-title {
      text-align: left;
    }

    .weather-simple {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 100%;
      width: 100%;
      gap: 16px;

      .now-text {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        line-height: normal;
        height: 100%;

        &.noLocation {
          justify-content: center;
        }
      }

      .now-icon {
        display: flex;
        align-items: center;
        justify-content: right;
      }

      &.nowNoLocation {
        justify-content: center;
        gap: 0;
      }
    }

    .weather-multiple {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
      width: 100%;

      &.noLocation {
        justify-content: center;
      }

      .multiple-items {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .item-container {
        display: flex;
        gap: 4px;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        height: 100%;

        .multiple-temperature {
          display: flex;
          flex-direction: column;
          justify-content: start;
          align-items: center;
          font-weight: bold;
        }

        .multiple-center {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          height: 100%;
        }

        .multiple-time {
          font-weight: bold;
          text-align: center;
        }
      }
    }
  }
</style>
